import { annotationHasValue, type Annotation } from "@/types/api/Annotation"

export interface AnnotationFieldAttribution {
  assignee: string
  updatedAt: string
}

export interface AnnotationAttribution {
  primary: AnnotationFieldAttribution
  valueFrom?: AnnotationFieldAttribution
  noteFrom?: AnnotationFieldAttribution
  jsonFrom?: AnnotationFieldAttribution
}

export type MergedAnnotation = Annotation & {
  attribution: AnnotationAttribution
}

const hasNonEmptyNote = (annotation: Annotation): boolean => {
  const note = annotation.annotations?.custom_explanation
  return typeof note === "string" && note.trim().length > 0
}

const hasJsonValue = (annotation: Annotation): boolean =>
  annotation.ground_truth_json !== null &&
  annotation.ground_truth_json !== undefined

const hasSubvalues = (annotation: Annotation): boolean => {
  const sv = annotation.ground_truth_subvalues_by_timestamp
  return Array.isArray(sv) && sv.length > 0
}

/**
 * Collapses a chain of annotations for a single (sim, metric) into a single
 * "merged latest" annotation. The most recent row wins for identity and any
 * field it has set; older rows gap-fill missing fields (value, note, json,
 * audio subvalues) so partial annotations created outside the chain are still
 * surfaced.
 *
 * Callers must pre-filter archived rows.
 */
export function mergeAnnotationsLatest(
  rows: Annotation[],
): MergedAnnotation | null {
  if (rows.length === 0) {
    return null
  }

  const sorted = [...rows].sort((a, b) => {
    const aTime = new Date(a.last_updated_at).getTime()
    const bTime = new Date(b.last_updated_at).getTime()
    return bTime - aTime
  })

  const base = sorted[0]
  const older = sorted.slice(1)

  const merged: MergedAnnotation = {
    ...base,
    attribution: {
      primary: {
        assignee: base.assignee,
        updatedAt: base.last_updated_at,
      },
    },
  }

  if (!annotationHasValue(base)) {
    const fillFrom = older.find(annotationHasValue)
    if (fillFrom) {
      merged.ground_truth_float_value = fillFrom.ground_truth_float_value
      merged.ground_truth_string_value = fillFrom.ground_truth_string_value
      merged.ground_truth_set_value = fillFrom.ground_truth_set_value
      merged.attribution.valueFrom = {
        assignee: fillFrom.assignee,
        updatedAt: fillFrom.last_updated_at,
      }
    }
  }

  if (!hasNonEmptyNote(base)) {
    const fillFrom = older.find(hasNonEmptyNote)
    if (fillFrom) {
      merged.annotations = {
        ...merged.annotations,
        custom_explanation: fillFrom.annotations?.custom_explanation ?? null,
      }
      merged.attribution.noteFrom = {
        assignee: fillFrom.assignee,
        updatedAt: fillFrom.last_updated_at,
      }
    }
  }

  if (!hasJsonValue(base)) {
    const fillFrom = older.find(hasJsonValue)
    if (fillFrom) {
      merged.ground_truth_json = fillFrom.ground_truth_json
      merged.attribution.jsonFrom = {
        assignee: fillFrom.assignee,
        updatedAt: fillFrom.last_updated_at,
      }
    }
  }

  if (!hasSubvalues(base)) {
    const fillFrom = older.find(hasSubvalues)
    if (fillFrom) {
      merged.ground_truth_subvalues_by_timestamp =
        fillFrom.ground_truth_subvalues_by_timestamp
    }
  }

  return merged
}

/**
 * Picks the "headline" annotation for a (sim, metric) — the one that should
 * lead the detail view and whose star you'd expect in the SimulationReviewTable.
 * Selection mirrors `groupAnnotationsBySimulation`'s primary rule: the row with
 * `priority === "PRIMARY"` wins; otherwise the most recently updated. The
 * chosen reviewer's rows are then merged among themselves via
 * `mergeAnnotationsLatest`, so the displayed value and note always come from
 * the same person — no cross-reviewer field pairing.
 *
 * Callers may pass already-filtered rows (e.g. project-scoped); archived rows
 * are dropped here so callers don't have to.
 */
export function selectPrimaryReviewerMerged(
  rows: Annotation[],
): MergedAnnotation | null {
  const active = rows.filter((r) => !r.archived)
  if (active.length === 0) {
    return null
  }
  const primary =
    active.find((r) => r.priority === "PRIMARY") ??
    [...active].sort((a, b) => {
      const timeDiff =
        new Date(b.last_updated_at).getTime() -
        new Date(a.last_updated_at).getTime()
      if (timeDiff !== 0) {
        return timeDiff
      }
      const createdDiff =
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      if (createdDiff !== 0) {
        return createdDiff
      }
      return b.id.localeCompare(a.id)
    })[0]
  const sameReviewer = primary.assignee
    ? active.filter((r) => r.assignee === primary.assignee)
    : [primary]
  const merged = mergeAnnotationsLatest(sameReviewer)
  if (!merged) {
    return null
  }
  // mergeAnnotationsLatest uses the newest row as the base, which can drop
  // PRIMARY priority if the chosen reviewer's PRIMARY row is older than a
  // STANDARD note row. Lift it back so the detail view shows the right star
  // state and the "set primary" click on this row is a no-op.
  return sameReviewer.some((r) => r.priority === "PRIMARY")
    ? { ...merged, priority: "PRIMARY" }
    : merged
}
